import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, ShieldCheck, CreditCard, Repeat, Lock, BarChart3, CheckCircle } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import ContactForm from "@/components/ContactForm";
import RelatedReviews from "@/components/RelatedReviews";
import LocationCrossLinks from "@/components/LocationCrossLinks";
import FormReviewCompanion from "@/components/FormReviewCompanion";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useProductViewed } from "@/utils/trackingHooks";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const gatewayHeroInitial = { opacity: 0, y: 20 };
const gatewayHeroAnimate = { opacity: 1, y: 0 };
const gatewayHeroTransition = { duration: 0.5, delay: 0.3 };

export default function PaymentGatewayPage() {
  useProductViewed("payment_gateway");
  return (
    <div>
      <SEOHead title="Payment Gateway for Subscription Services" description="Discover secure payment gateways with CanDo Payments. Easily manage recurring billing and ensure smooth transactions for your clients. Get in touch!" path="/payment-gateway" service={{ name: "Payment Gateway", description: "Hosted PCI-DSS payment gateway for UK merchants. Tokenisation, fraud screening, and same-day onboarding.", serviceType: "Payment Gateway" }} />
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <Breadcrumbs className="mb-5" />
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Payment Gateway</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">Start accepting online payments <span className="gradient-text">in 24 hours</span></motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">Enterprise-grade gateway powered by Elavon. PCI compliant, 3D Secure, and plugs straight into WooCommerce, Shopify, Magento and more — with real UK humans on support.</motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <Link to="/contact?product=Payment%20Gateway&category=payment-gateway&source=gateway-page" className="cta-primary text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-all btn-shine">Get gateway pricing <ArrowRight size={16} /></Link>
                <Link to="/quote/online" className="bg-brand-pink-light text-brand-pink hover:bg-brand-pink/15 border border-brand-pink/30 font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors">Estimate my costs</Link>
              </motion.div>
            </motion.div>
            <motion.div initial={gatewayHeroInitial} animate={gatewayHeroAnimate} transition={gatewayHeroTransition} className="hidden lg:block">
              <div className="bg-white rounded-2xl shadow-xl border border-brand-light-grey/60 p-5 max-w-sm mx-auto">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2.5 h-2.5 rounded-full bg-brand-green" />
                  <span className="text-xs font-semibold text-brand-grey">Gateway Status: Live</span>
                </div>
                <div className="space-y-2.5">
                  {[
                    { label: "WooCommerce", status: "Connected" },
                    { label: "Shopify", status: "Connected" },
                    { label: "Custom API", status: "Ready" },
                  ].map((p) => (
                    <div key={p.label} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
                      <span className="font-semibold text-brand-black">{p.label}</span>
                      <span className="text-xs font-bold text-brand-green">{p.status}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-brand-light-grey/60 flex items-center gap-2 text-xs text-brand-grey">
                  <Lock size={12} /> PCI DSS Level 1 Compliant
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      <section className="py-12 bg-white border-b border-brand-light-grey/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, label: "PCI DSS Compliant" },
            { icon: Lock, label: "3D Secure 2.x" },
            { icon: Globe, label: "Multi-Currency" },
            { icon: BarChart3, label: "Real-Time Dashboard" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3 p-4 rounded-xl bg-brand-aqua-light">
              <item.icon size={20} className="text-brand-aqua flex-shrink-0" />
              <span className="text-sm font-semibold text-brand-black">{item.label}</span>
            </div>
          ))}
        </div>
      </section>
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: CreditCard, title: "All Major Cards & Wallets", desc: "Accept Visa, Mastercard, Amex, Apple Pay and Google Pay through a single integration." },
            { icon: Lock, title: "Hosted or API Integration", desc: "Use hosted payment pages for fast go-live, or the full API for complete checkout control." },
            { icon: Repeat, title: "Recurring Billing", desc: "Automate subscriptions and memberships with tokenised stored-shopper payments." },
            { icon: Globe, title: "Multi-Currency", desc: "Accept payments in 100+ currencies — ideal for businesses selling internationally." },
            { icon: ShieldCheck, title: "Built-In Fraud Protection", desc: "AI-powered fraud scoring, velocity checks and real-time monitoring as standard." },
            { icon: BarChart3, title: "Back-Office Dashboard", desc: "Track transactions, settlements, refunds and chargebacks from one clear interface." },
          ].map((f) => (
            <div key={f.title} className="bg-white border border-brand-light-grey rounded-2xl p-6 service-card">
              <f.icon size={24} className="text-brand-aqua mb-4" />
              <h3 className="font-heading font-bold text-brand-black mb-2">{f.title}</h3>
              <p className="text-sm text-brand-grey leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Supported platforms — merchant-facing */}
      <section className="py-16 bg-white" data-testid="gateway-platforms">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Supported Platforms</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight">Works with your e-commerce platform</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto">
            {[
              { name: "WooCommerce", slug: "woocommerce" },
              { name: "Shopify", slug: "shopify" },
              { name: "Magento 2", slug: "magento" },
              { name: "PrestaShop", slug: "prestashop" },
              { name: "BigCommerce", slug: "bigcommerce" },
              { name: "OpenCart", slug: "opencart" },
              { name: "Drupal", slug: "drupal" },
              { name: "Custom API", slug: null },
            ].map((p) => (
              <Link key={p.name} to={p.slug ? `/integrations/${p.slug}` : "/contact?product=Custom%20API&category=payment-gateway&source=gateway-page"} className="flex items-center justify-center gap-2 py-3 px-4 bg-gray-50 border border-brand-light-grey rounded-xl text-sm font-semibold text-brand-black hover:bg-brand-aqua-light hover:border-brand-aqua/30 hover:text-brand-aqua-dark transition-all">
                {p.name}
              </Link>
            ))}
          </div>
          <p className="text-center text-sm text-brand-grey mt-6">
            <Link to="/integrations" className="text-brand-aqua font-semibold hover:underline">View all platform integrations →</Link>
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-6">Why choose CanDo for your payment gateway?</h2>
            <div className="space-y-3">
              {["Powered by Elavon — enterprise-grade infrastructure", "Next-day settlement into your UK bank account", "Plug-ins for WooCommerce, Shopify, Magento and more", "Clear, transparent pricing with no hidden fees", "UK-based support — real humans, not chatbots", "Sandbox access for testing before going live"].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-brand-black"><CheckCircle size={16} className="text-brand-aqua flex-shrink-0" />{item}</div>
              ))}
            </div>
          </div>
          <div className="space-y-5">
            <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
              <h3 className="font-heading text-xl font-bold text-brand-black mb-2">Set up your payment gateway</h3>
              <p className="text-sm text-brand-grey mb-4">Order before 2pm — your gateway can be live the next working day.</p>
              <ContactForm />
            </div>
            <FormReviewCompanion product="payment-gateway" testId="payment-gateway-form-review" />
          </div>
        </div>
      </section>

      <LocationCrossLinks />
      <RelatedReviews
        product="payment-gateway"
        eyebrow="Customer Stories"
        heading="See what our customers say about our payment gateway"
        subheading="Real feedback from UK e-commerce teams using CanDo's gateway."
        testId="payment-gateway-related-reviews"
      />
    </div>
  );
}
